import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, DataTable, StatusBadge, Avatar, Button,
  LoadingState, EmptyState, PremiumModal,
} from "components/ui";

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M8 16H3v5" />
  </svg>
);

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING", label: "Pending" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const daysLeft = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.ceil((d - new Date()) / 86400000);
};

const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency ?? ""}`;
  }
};

const SubscriptionsList = () => {
  const axiosPrivate = useAxiosPrivate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null); // subscription being cancelled
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const fetchSubscriptions = async (status = statusFilter) => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get("/api/admin/subscriptions", {
        params: status ? { status } : {},
      });
      setSubscriptions(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosPrivate, statusFilter]);

  const filtered = subscriptions; // filtering happens server-side via ?status=... on GET /api/admin/subscriptions

  const openCancelModal = (subscription) => {
    setCancelTarget(subscription);
    setCancelReason("");
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axiosPrivate.patch(`/api/admin/subscriptions/${cancelTarget.id}/cancel`, {
        cancellationReason: cancelReason || undefined,
      });
      toast.success(`Subscription for ${cancelTarget.tenantName ?? "tenant"} cancelled`);
      setCancelTarget(null);
      fetchSubscriptions();
    } catch (err) {
      console.error(err);
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading subscriptions…" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PremiumCard>
        <DataTable
          title="Subscriptions"
          subtitle={`${filtered.length} subscription${filtered.length === 1 ? "" : "s"}`}
          toolbar={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  height: 32,
                  padding: "0 28px 0 10px",
                  borderRadius: 8,
                  border: "1px solid var(--border-subtle)",
                  background: "var(--surface, #fff)",
                  color: "var(--text-primary, inherit)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Button variant="ghost" size="sm" leftIcon={<IconRefresh />} onClick={() => fetchSubscriptions(statusFilter)}>
                Refresh
              </Button>
              
            </div>
          }
          search
          searchKeys={["tenantName", "planName", "status"]}
          paginate
          pageSize={10}
          data={filtered}
          rowKey={(r) => r.id}
          emptyTitle="No subscriptions found"
          emptyDescription="Try a different status filter, or create a new subscription."
          columns={[
            {
              key: "tenantName", header: "Tenant",
              render: (s) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={s.tenantName} size="sm" />
                  <span style={{ fontWeight: 600 }}>{s.tenantName ?? "—"}</span>
                </div>
              ),
            },
            {
              key: "planName", header: "Plan",
              render: (s) => <StatusBadge value={s.planName ?? "—"} />,
            },
            {
              key: "billingCycle", header: "Cycle",
              render: (s) => (
                <span className="t-muted" style={{ fontSize: 13 }}>
                  {s.billingCycle === "MONTHLY" ? "Monthly" : s.billingCycle === "YEARLY" ? "Yearly" : s.billingCycle ?? "—"}
                </span>
              ),
            },
            {
              key: "pricePaid", header: "Price", align: "right",
              render: (s) => <strong>{formatMoney(s.pricePaid, s.currency)}</strong>,
            },
            {
              key: "startDate", header: "Started",
              render: (s) => (s.startDate ? new Date(s.startDate).toLocaleDateString() : "—"),
            },
            {
              key: "renewalDate", header: "Renewal",
              render: (s) => {
                if (!s.renewalDate) return "—";
                const days = daysLeft(s.renewalDate);
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span>{new Date(s.renewalDate).toLocaleDateString()}</span>
                    <span className={days !== null && days <= 7 ? "t-danger" : "t-muted"} style={{ fontSize: 11 }}>
                      {days !== null ? `${days}d left` : ""}
                    </span>
                  </div>
                );
              },
            },
            {
              key: "endDate", header: "Ends",
              render: (s) => (s.endDate ? new Date(s.endDate).toLocaleDateString() : "—"),
            },
            {
              key: "status", header: "Status",
              render: (s) => <StatusBadge value={s.status} dot />,
            },
            {
              key: "actions", header: "", align: "right",
              render: (s) => (
                s.status === "ACTIVE" || s.status === "PENDING" ? (
                  <Button variant="ghost" size="sm" leftIcon={<IconX />} onClick={() => openCancelModal(s)}>
                    Cancel
                  </Button>
                ) : (
                  <span className="t-muted" style={{ fontSize: 12 }}>
                    {s.cancelledAt ? `Cancelled ${new Date(s.cancelledAt).toLocaleDateString()}` : "—"}
                  </span>
                )
              ),
            },
          ]}
        />
      </PremiumCard>

      {cancelTarget && (
        <PremiumModal
          title="Cancel subscription"
          onClose={() => setCancelTarget(null)}
        >
          <p style={{ marginBottom: 12 }}>
            Cancel the subscription for <strong>{cancelTarget.tenantName ?? "this tenant"}</strong>
            {" "}on the <strong>{cancelTarget.planName ?? ""}</strong> plan?
          </p>
          <textarea
            placeholder="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
            style={{ width: "100%", resize: "vertical" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button variant="ghost" size="sm" onClick={() => setCancelTarget(null)} disabled={cancelling}>
              Back
            </Button>
            <Button variant="danger" size="sm" onClick={confirmCancel} disabled={cancelling}>
              {cancelling ? "Cancelling…" : "Confirm cancellation"}
            </Button>
          </div>
        </PremiumModal>
      )}

      
    </PageContainer>
  );
};

export default SubscriptionsList;